<!-- eslint-disable vue/no-v-html -->
<template>
  <component
    :is="linkValue ? 'a' : 'div'"
    class="tw-image"
    :class="{ 'tw-live': !isEditorMode }"
    :style="[styleWithoutWidthHeight, cssVars]"
    :target="targetValue"
    :href="linkValue"
  >
    <img
      :style="[
        `--imageH: ${style.height}`,
        `--imageW: ${style.width}`,
        styleWithoutWidthHeight
      ]"
      :src="imageUrl"
      v-bind="{ ...attr, ...element.attr}"
      role="presentation"
      :alt="element.id"
    />
  </component>
</template>

<script lang="ts" setup>
import { type PropType, computed, toRef } from 'vue';
import type { Element as ElementType } from '@zotlo-shared/types/model';
import { getAssetFromCdn, mergeDeep } from '@zotlo-shared/utils/helpers';
import type { ScreenSize } from '@zotlo-shared/types/options';

const props = defineProps({
  tagName: { type: String, default: 'img' },
  value: { type: String, default: '' },
  style: { type: Object, default: () => ({}) },
  attr: { type: Object as PropType<ElementType<'image'>['attr']>, default: () => ({}) },
  element: { type: Object as PropType<ElementType<'image'>>, default: () => ({}) },
  isEditorMode: { type: Boolean, default: false },
  sizes: { type: Object, default: () => ({}) },
});

const element = toRef(props, 'element');

const linkValue = computed(() => {return props.element?.attr?.href || props.element?.attr['data-href']})
const targetValue = computed(() => {return props.element?.attr?.target })

const styleWithoutWidthHeight = computed(() => {
  const style = JSON.parse(JSON.stringify(props.style));
  delete style.width;
  delete style.height;
  delete style.objectFit;
  return style;
})

function isURL(str: string) {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
}

const elStyle = computed(() => {
  const sizes = mergeDeep({
    mobile: {
      height: 'auto',
      width: props.style.width || 'auto',
      objectFit: 'contain',
    },
    tablet: {
      height: 'auto',
      width: props.style.width || 'auto',
      objectFit: 'contain',
    },
    desktop: {
      height: props.style.height || 'auto',
      width: props.style.width || 'auto',
      objectFit: 'contain',
    },
  }, props.element.options.sizes || {});

  const obj = Object.entries(sizes).reduce((acc, [key, value]) => {
    acc[key as ScreenSize] = {
      height: value.autoHeight ? 'auto' : (value.height + 'px'),
      width: value.width === 'auto' ? undefined : value.width + 'px',
      objectFit: value.objectFit || 'contain',
    }
    return acc;
  }, {} as Record<ScreenSize, {height: string | number | undefined; width: string | number | undefined; objectFit: string}>);

  return obj;
});


const cssVars = computed(() => ({
  '--image-mobile-height': elStyle.value.mobile?.height,
  '--image-mobile-width': elStyle.value.mobile?.width,
  '--image-mobile-objectFit': elStyle.value.mobile?.objectFit,
  '--image-tablet-height': elStyle.value.tablet?.height,
  '--image-tablet-width': elStyle.value.tablet?.width,
  '--image-tablet-objectFit': elStyle.value.tablet?.objectFit,
  '--image-desktop-height': elStyle.value.desktop?.height,
  '--image-desktop-width': elStyle.value.desktop?.width,
  '--image-desktop-objectFit': elStyle.value.desktop?.objectFit,
  '--image-old-objectFit': props.style.objectFit,
}));

const imageUrl = computed(() => {
  return isURL(props.value) ? props.value : getAssetFromCdn(props.value)
});
</script>

<style lang="postcss" scoped>
.tw-image {
  position: relative;
  display: flex;
  justify-content: center;
  max-width: 100%;
  height: var(--image-mobile-height);
  width: var(--image-mobile-width);

  --oldObjectfit: var(--image-old-objectFit);

  img {
    width: 100%;
    object-fit: var(--image-mobile-objectFit);
    object-position: center;
  }

  &.ignore-h-auto {
    img {
      object-fit: var(--oldObjectfit, 'contain');
    }
  }

  /**
  * If element has this class, containing img will be fixed height as percent,
  * it is necessary trick for equal height columns
  */
  &.has-fixed-height-image img {
    height: 100% !important;
  }

  @media (min-width: 768px) {
    height: var(--image-tablet-height);
    width: var(--image-tablet-width);

    img {
      object-fit: var(--image-tablet-objectFit);
    }
  }

  @media (min-width: 1024px) {
    height: var(--image-desktop-height);
    width: var(--image-desktop-width);

    img {
      object-fit: var(--image-desktop-objectFit);
    }
  }
}

.desktop {
  .tw-image {
    height: var(--image-desktop-height);
    width: var(--image-desktop-width);

    img {
      object-fit: var(--image-desktop-objectFit);
    }
  }
}

.tablet {
  .tw-image {
    height: var(--image-tablet-height);
    width: var(--image-tablet-width);

    img {
      object-fit: var(--image-tablet-objectFit);
    }
  }
}

.mobile {
  .tw-image {
    height: var(--image-mobile-height);
    width: var(--image-mobile-width);

    img {
      object-fit: var(--image-mobile-objectFit);
    }
  }
}
</style>
